import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getPolicyPremiumTotal, getPolicyGrandTotal, getServicesTotal } from "../utils/policyCalc";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Separator } from "../components/ui/separator";
import {
  ArrowLeft,
  User,
  Building2,
  FileText,
  Calendar,
  Euro,
  Car,
  Home,
  Heart,
  Shield,
  AlertCircle,
  CheckCircle,
  Pause,
  Play,
  RefreshCw,
  Download,
  Pencil,
  Phone,
  MessageCircle,
  Mail,
  Clock,
  CalendarDays,
  Receipt,
  Loader2,
  Plus,
  Trash2,
  Package,
  Undo2,
  RotateCcw,
  XCircle
} from "lucide-react";
import { toast } from "sonner";
import { parseISO, isValid, differenceInDays } from "date-fns";
import DocumentsDialog from "../components/DocumentsDialog";
import { DocumentPreviewCard } from "../components/DocumentPreviewCard";
import { RenewalPolicyDialog } from "../components/policies";
import { SuspendPolicyDialog, ReactivatePolicyDialog } from "../components/policies/SuspendPolicyDialog";
import { ClosePolicyDialog } from "../components/policies/ClosePolicyDialog";
import { NOT_RENEWED_REASONS, SUSPENDABLE_TYPES, getPolicyTypeInfo, isVehiclePolicyType } from "../components/policies";
import { Breadcrumb } from "../components/Breadcrumb";
import AuditHistory from "../components/AuditHistory";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { formatCurrency, formatDate } from "../lib/format";
import { useConferma } from "../components/ConfirmProvider";

// Icone per tipo polizza
const getPolicyTypeIcon = (type) => {
  const iconClass = "h-5 w-5";
  switch (String(type || "").toLowerCase()) {
    case 'auto':
    case 'moto':
    case 'autocarri':
    case 'altri_veicoli':
    // "cvt" = Corpi Veicoli Terrestri: ramo veicolo (kasko del mezzo).
    case 'cvt':
      return <Car className={iconClass} />;
    case 'casa':
      return <Home className={iconClass} />;
    case 'vita':
    case 'salute':
    case 'infortuni':
      return <Heart className={iconClass} />;
    default:
      return <Shield className={iconClass} />;
  }
};

// Badge stato polizza
const getStatusBadge = (policy) => {
  if (policy.status === "cancelled") {
    return <Badge variant="outline" className="bg-gray-200 text-gray-800 border-gray-400 text-sm px-3 py-1">⛔ Chiusa</Badge>;
  }
  if (policy.suspended) {
    return <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-sm px-3 py-1">⏸️ Sospesa</Badge>;
  }
  if (policy.not_renewed || policy.status === "not_renewed") {
    return <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-sm px-3 py-1">❌ Non Rinnovata</Badge>;
  }
  if (policy.renewed) {
    return <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300 text-sm px-3 py-1">🔄 Rinnovata</Badge>;
  }
  if (policy.status === 'expired') {
    return <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-sm px-3 py-1">⚠️ Scaduta</Badge>;
  }
  return <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 text-sm px-3 py-1">✅ Attiva</Badge>;
};

// Calcola giorni alla scadenza
const getDaysToExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  try {
    const expiry = parseISO(expiryDate);
    if (!isValid(expiry)) return null;
    return differenceInDays(expiry, new Date());
  } catch {
    return null;
  }
};

export default function PolicyDetailPage() {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const conferma = useConferma();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  // Se il caricamento fallisce non si deve leggere "Polizza non trovata"
  // (sembrerebbe una perdita di dati) ne' essere buttati fuori dalla pagina.
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Documents dialog
  const [documentsOpen, setDocumentsOpen] = useState(false);
  
  // Additional services
  const [showAddService, setShowAddService] = useState(false);
  const [newService, setNewService] = useState({ provider: "", description: "", cost: "" });
  const [savingService, setSavingService] = useState(false);
  const [servicesCatalog, setServicesCatalog] = useState([]);

  // Fetch services catalog
  useEffect(() => {
    axios.get("/api/services").then(res => setServicesCatalog(res.data || [])).catch(() => {});
  }, []);
  
  // Renewal dialog
  const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
  // Dopo il rinnovo: nuova polizza creata, con la possibilità di allegare subito il documento
  const [renewalResult, setRenewalResult] = useState(null);
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const [undoingSemester, setUndoingSemester] = useState(false);
  const [undoingNotRenewed, setUndoingNotRenewed] = useState(false);
  // 19/10/2026 — chiusura anticipata
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [undoingClose, setUndoingClose] = useState(false);
  const [undoingVehicle, setUndoingVehicle] = useState(false);
  const [renewalData, setRenewalData] = useState({
    renewalType: "same_company",
    newCompany: "",
    notRenewedReason: "",
    newPremium: "",
    newConsultationFee: ""
  });
  
  // Suspend dialog
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspensionData, setSuspensionData] = useState({
    suspension_reminder: "",
    suspension_reason: ""
  });
  
  // Reactivate dialog
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [reactivationData, setReactivationData] = useState({
    new_start_date: "",
    new_expiry_date: ""
  });

  // Replace vehicle dialog
  const [replaceVehicleOpen, setReplaceVehicleOpen] = useState(false);
  const EMPTY_VEHICLE_FORM = {
    new_plate: "", notes: "", new_vehicle_desc: "", effective_date: "",
    appendix_number: "", adj_mode: "none", adj_value: "", consultation_adjustment: ""
  };
  const [replaceVehicleData, setReplaceVehicleData] = useState(EMPTY_VEHICLE_FORM);
  const [savingVehicle, setSavingVehicle] = useState(false);

  // v3.9.13 — variazione in corso d'anno (appendice di variazione):
  // il cliente aggiunge o toglie garanzie durante l'anno
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const EMPTY_ADJUSTMENT_FORM = {
    description: "",
    effective_date: "",
    premium_adjustment: "",
    consultation_adjustment: "",
    appendix_number: "",
    notes: "",
  };
  const [adjustmentData, setAdjustmentData] = useState(EMPTY_ADJUSTMENT_FORM);
  const [savingAdjustment, setSavingAdjustment] = useState(false);

  useEffect(() => {
    fetchPolicy();
  }, [policyId]);

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/policies/${policyId}`);
      setPolicy(response.data);
      
      // Fetch client info
      if (response.data.client_id) {
        const clientResponse = await axios.get(`/api/clients/${response.data.client_id}`);
        setClient(clientResponse.data);
      }
    } catch (error) {
      setLoadError(true);
      toast.error("Errore nel caricamento della polizza");
    } finally {
      setLoading(false);
    }
  };

  const handleRenewal = async () => {
    setSaving(true);
    try {
      if (renewalData.renewalType === "not_renewed") {
        await axios.post(`/api/policies/${policy.policy_id}/mark-not-renewed`, {
          reason: renewalData.notRenewedReason
        });
        toast.success("Polizza segnata come non rinnovata");
      } else if (renewalData.renewalType === "renew_semester") {
        await axios.post(`/api/policies/${policy.policy_id}/renew-semester`);
        toast.success("Rata semestrale rinnovata (+6 mesi)");
      } else {
        const res = await axios.post(`/api/policies/${policy.policy_id}/renew`, {
          same_company: renewalData.renewalType === "same_company",
          new_company: renewalData.newCompany || null,
          new_premium: renewalData.newPremium ? parseFloat(renewalData.newPremium) : null,
          new_consultation_fee: renewalData.newConsultationFee ? parseFloat(renewalData.newConsultationFee) : null,
          // 17/09/2026 — frequenza scelta per il NUOVO contratto (+ data prima rata se semestrale)
          new_payment_frequency: renewalData.newPaymentFrequency
            || (policy.payment_frequency === "semiannual" ? "semestral" : "annual"),
          new_semiannual_expiry: renewalData.newSemiannualExpiry || null
        });
        toast.success("Polizza rinnovata con successo");
        if (res.data?.new_policy_id) {
          // resto aperto: c'è da allegare il documento della nuova polizza
          setRenewalResult(res.data);
          fetchPolicy();
          setSaving(false);
          return;
        }
      }
      setRenewalDialogOpen(false);
      fetchPolicy();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel rinnovo");
    } finally {
      setSaving(false);
    }
  };

  // Allega il documento della polizza appena rinnovata (locale + MEGA)
  const allegaDocumentoRinnovo = async (file) => {
    if (!file || !renewalResult?.new_policy_id) return;
    setUploadingAttach(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(
        `/api/documents/upload?entity_type=policy&entity_id=${renewalResult.new_policy_id}&storage=both&auto_rename=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Documento allegato alla nuova polizza");
      setRenewalResult(null);
      setRenewalDialogOpen(false);
      fetchPolicy();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Non riesco ad allegare il documento");
    } finally {
      setUploadingAttach(false);
    }
  };

  const saltaAllegato = () => {
    setRenewalResult(null);
    setRenewalDialogOpen(false);
    fetchPolicy();
  };

  // v3.9.12 — annulla il pagamento della rata semestrale (click sbagliato)
  const handleUndoSemester = async () => {
    if (!(await conferma({
      titolo: "Annullare il pagamento della rata semestrale?",
      descrizione: "La rata tornerà \"da pagare\" e la scadenza verrà riportata al periodo giusto. L'operazione viene registrata nello storico modifiche.",
    }))) return;

    setUndoingSemester(true);
    try {
      const res = await axios.post(`/api/policies/${policy.policy_id}/unmark-semester`);
      const rata = res.data?.rata_annullata;
      toast.success(
        rata?.period
          ? `Pagamento annullato. Rata del ${rata.period} di nuovo da pagare.`
          : "Pagamento rata annullato."
      );
      setRenewalDialogOpen(false);
      fetchPolicy();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'annullamento del pagamento");
    } finally {
      setUndoingSemester(false);
    }
  };

  // v3.9.12 — annulla "non rinnovata" (click sbagliato)
  const handleUndoNotRenewed = async () => {
    if (!(await conferma({
      titolo: "Annullare il \"non rinnovata\"?",
      descrizione: "La polizza torna in stato normale (attiva, o scaduta se la data è passata) e ricompare nei report. L'operazione viene registrata nello storico modifiche.",
    }))) return;

    setUndoingNotRenewed(true);
    try {
      const res = await axios.post(`/api/policies/${policy.policy_id}/unmark-not-renewed`);
      toast.success(`Polizza ripristinata (stato: ${res.data?.status === "expired" ? "scaduta" : "attiva"})`);
      fetchPolicy();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel ripristino");
    } finally {
      setUndoingNotRenewed(false);
    }
  };

  // 19/10/2026 — annulla una chiusura fatta per errore (polizza sbagliata)
  const handleUndoClose = async () => {
    if (!(await conferma({
      titolo: "Annullare la chiusura?",
      descrizione: "Da usare solo se hai chiuso la polizza per errore: torna com'era prima (attiva, o scaduta se la data è passata). L'operazione resta nello storico modifiche.",
    }))) return;
    setUndoingClose(true);
    try {
      await axios.post(`/api/policies/${policy.policy_id}/reopen`);
      toast.success("Chiusura annullata");
      fetchPolicy();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'annullare la chiusura");
    } finally {
      setUndoingClose(false);
    }
  };

  // v3.9.12 — annulla l'ultima sostituzione mezzo
  const handleUndoReplaceVehicle = async () => {
    const last = (policy.vehicle_history || [])[policy.vehicle_history.length - 1];
    if (!(await conferma({
      titolo: "Annullare l'ultima sostituzione mezzo?",
      descrizione:
        `Targa: ${last?.new_plate || "?"} → ${last?.plate || "?"}\n` +
      "Vengono ripristinati targa, premio e consulenza precedenti e il conguaglio viene rimosso dai report. " +
      "L'operazione è tracciata nello storico modifiche.",
    }))) return;
    setUndoingVehicle(true);
    try {
      const res = await axios.post(`/api/policies/${policy.policy_id}/undo-replace-vehicle`);
      toast.success(`Sostituzione annullata: targa ripristinata a ${res.data?.license_plate || "—"}`);
      fetchPolicy();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'annullamento della sostituzione");
    } finally {
      setUndoingVehicle(false);
    }
  };

  const handleSuspend = async () => {
    try {
      await axios.post(`/api/policies/${policy.policy_id}/suspend`, suspensionData);
      toast.success("Polizza sospesa");
      setSuspendDialogOpen(false);
      fetchPolicy();
    } catch (error) {
      toast.error("Errore nella sospensione");
    }
  };

  const handleReactivate = async () => {
    try {
      const { data } = await axios.post(`/api/policies/${policy.policy_id}/reactivate`, reactivationData);
      toast.success(`Polizza riattivata — nuova scadenza ${formatDate(data?.new_expiry_date, "-")}`);
      setReactivateDialogOpen(false);
      fetchPolicy();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella riattivazione");
    }
  };

  const handleReplaceVehicle = async () => {
    if (!replaceVehicleData.new_plate.trim()) {
      toast.error("La nuova targa è obbligatoria");
      return;
    }
    setSavingVehicle(true);
    try {
      const payload = {
        new_plate: replaceVehicleData.new_plate.trim(),
        notes: replaceVehicleData.notes.trim() || null,
        new_vehicle_desc: replaceVehicleData.new_vehicle_desc.trim() || null,
        effective_date: replaceVehicleData.effective_date || null,
        appendix_number: replaceVehicleData.appendix_number.trim() || null,
      };
      // conguaglio premio: una sola modalità per volta
      const val = parseFloat(replaceVehicleData.adj_value);
      if (!isNaN(val) && replaceVehicleData.adj_mode !== "none") {
        if (replaceVehicleData.adj_mode === "percent") payload.adjustment_percent = val;
        else if (replaceVehicleData.adj_mode === "amount") payload.premium_adjustment = val;
        else if (replaceVehicleData.adj_mode === "full") payload.new_premium = val;
      }
      const cons = parseFloat(replaceVehicleData.consultation_adjustment);
      if (!isNaN(cons)) payload.consultation_adjustment = cons;

      await axios.post(`/api/policies/${policy.policy_id}/replace-vehicle`, payload);
      toast.success("Mezzo sostituito con successo");
      setReplaceVehicleOpen(false);
      setReplaceVehicleData(EMPTY_VEHICLE_FORM);
      fetchPolicy();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella sostituzione");
    } finally {
      setSavingVehicle(false);
    }
  };

  // v3.9.13 — registra una variazione in corso d'anno (appendice)
  const handleAddAdjustment = async () => {
    const descrizione = adjustmentData.description.trim();
    const premio = parseFloat(adjustmentData.premium_adjustment);
    const cons = parseFloat(adjustmentData.consultation_adjustment);
    if (!descrizione && !premio && !cons) {
      toast.error("Indica cosa cambia oppure la differenza di prezzo");
      return;
    }
    setSavingAdjustment(true);
    try {
      const res = await axios.post(`/api/policies/${policy.policy_id}/add-adjustment`, {
        description: descrizione || null,
        effective_date: adjustmentData.effective_date || null,
        premium_adjustment: isNaN(premio) ? 0 : premio,
        consultation_adjustment: isNaN(cons) ? 0 : cons,
        appendix_number: adjustmentData.appendix_number.trim() || null,
        notes: adjustmentData.notes.trim() || null,
      });
      toast.success(
        `Variazione registrata: premio ${formatCurrency(res.data.premium_before)} → ${formatCurrency(res.data.premium_after)}`
      );
      setAdjustmentOpen(false);
      setAdjustmentData(EMPTY_ADJUSTMENT_FORM);
      fetchPolicy();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella registrazione della variazione");
    } finally {
      setSavingAdjustment(false);
    }
  };

  const handleUndoAdjustment = async () => {
    if (!(await conferma({
      titolo: "Annullare l'ultima variazione in corso d'anno?",
      descrizione:
        "Il premio e la consulenza tornano come prima della variazione e la differenza non viene più conteggiata nel mese.",
    }))) return;
    setSavingAdjustment(true);
    try {
      const res = await axios.post(`/api/policies/${policy.policy_id}/undo-adjustment`);
      toast.success(
        `Variazione annullata: premio ${formatCurrency(res.data.premium_before)} → ${formatCurrency(res.data.premium_after)}`
      );
      fetchPolicy();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'annullamento della variazione");
    } finally {
      setSavingAdjustment(false);
    }
  };

  const handleCall = () => {
    if (client?.phone) {
      window.location.href = `tel:${client.phone}`;
    } else {
      toast.error("Nessun numero di telefono disponibile");
    }
  };

  const handleWhatsApp = () => {
    if (client?.phone) {
      const cleanPhone = client.phone.replace(/\D/g, '');
      const phone = cleanPhone.startsWith('39') ? cleanPhone : `39${cleanPhone}`;
      const message = encodeURIComponent(
        `Buongiorno ${client.name}, la contatto in merito alla sua polizza ${policy.company} n. ${policy.policy_number}. Cordiali saluti.`
      );
      window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    } else {
      toast.error("Nessun numero di telefono disponibile");
    }
  };

  const handleEmail = () => {
    if (client?.email) {
      const subject = encodeURIComponent(`Polizza ${policy.company} n. ${policy.policy_number}`);
      const body = encodeURIComponent(
        `Gentile ${client.name},\n\nLa contatto in merito alla Sua polizza ${policy.company} n. ${policy.policy_number}.\n\nCordiali saluti`
      );
      window.location.href = `mailto:${client.email}?subject=${subject}&body=${body}`;
    } else {
      toast.error("Nessun indirizzo email disponibile");
    }
  };

  const getClientName = () => client?.name || "Cliente";

  const handleAddService = async () => {
    if (!newService.provider.trim() || !newService.description.trim()) {
      toast.error("Fornitore e descrizione sono obbligatori");
      return;
    }
    setSavingService(true);
    try {
      await axios.post(`/api/policies/${policyId}/services`, {
        provider: newService.provider.trim(),
        description: newService.description.trim(),
        cost: parseFloat(newService.cost) || 0
      });
      toast.success("Servizio aggiunto");
      setNewService({ provider: "", description: "", cost: "" });
      setShowAddService(false);
      fetchPolicy();
    } catch (error) {
      toast.error("Errore nell'aggiunta del servizio");
    } finally {
      setSavingService(false);
    }
  };

  const handleRemoveService = async (index) => {
    try {
      await axios.delete(`/api/policies/${policyId}/services/${index}`);
      toast.success("Servizio rimosso");
      fetchPolicy();
    } catch (error) {
      toast.error("Errore nella rimozione del servizio");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="policy-detail-loading">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
          <Skeleton className="h-80 w-full" />
        </div>
      </div>
    );
  }

  if (!policy) {
    if (loadError) {
      return (
        <div className="text-center py-12 space-y-3">
          <AlertCircle className="h-12 w-12 mx-auto text-amber-500" />
          <h2 className="text-xl font-semibold">Non riesco a caricare la polizza</h2>
          <p className="text-sm text-muted-foreground">
            I dati non sono andati persi: e' il collegamento che non ha risposto.
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button onClick={fetchPolicy}>
              <RefreshCw className="h-4 w-4 mr-2" /> Riprova
            </Button>
            <Button variant="outline" onClick={() => navigate("/policies")}>
              Torna alla lista polizze
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold">Polizza non trovata</h2>
        <Button variant="link" onClick={() => navigate("/policies")}>
          Torna alla lista polizze
        </Button>
      </div>
    );
  }

  const daysToExpiry = getDaysToExpiry(policy.expiry_date);
  const isExpiringSoon = daysToExpiry !== null && daysToExpiry <= 30 && daysToExpiry >= 0;
  const isExpired = daysToExpiry !== null && daysToExpiry < 0;
  const policyTypeInfo = getPolicyTypeInfo(policy.policy_type);
  // v3.9.5: rami VEICOLO — lista canonica condivisa (components/policies,
  // allineata a services/regole_polizza.TIPI_POLIZZA_VEICOLO, include "cvt").
  const isVehiclePolicy = isVehiclePolicyType(policy.policy_type) || Boolean(policy.license_plate);
  const isClosed = policy.status === "cancelled";
  const isSuspendable = isVehiclePolicy && !isClosed;

  return (
    <div className="space-y-6" data-testid="policy-detail-page">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: "Polizze", href: "/policies" },
        ...(client ? [{ label: client.name, href: `/clients/${client.client_id}` }] : []),
        { label: policy.policy_number }
      ]} />

      {/* Polizza STORICA: sostituita da un rinnovo. I suoi documenti restano qui. */}
      {policy.renewed && policy.renewed_policy_id && (
        <div className="rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-950/30 p-4" data-testid="banner-policy-storica">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="font-medium text-blue-900 dark:text-blue-200">🕘 Polizza storica</p>
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Questa polizza è stata <b>rinnovata</b>: in elenco vedi solo quella attuale.
                Qui restano i suoi dati e i <b>suoi documenti</b>.
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => navigate(`/policies/${policy.renewed_policy_id}`)}
              data-testid="apri-polizza-attuale-btn"
            >
              Apri la polizza attuale
            </Button>
          </div>
        </div>
      )}

      {/* Polizza CHIUSA in anticipo (vendita veicolo, demolizione...) */}
      {policy.status === "cancelled" && (
        <div className="rounded-lg border border-gray-400 bg-gray-100 dark:bg-gray-900/40 p-4" data-testid="banner-policy-chiusa">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="font-medium">⛔ Polizza chiusa il {formatDate(policy.closure_date, "-")}</p>
              <p className="text-sm text-muted-foreground">
                Motivo: <b>{policy.closure_reason || "-"}</b>
                {policy.closure_refund != null && <> · Rimborso: <b>{formatCurrency(policy.closure_refund)}</b></>}
              </p>
              {policy.closure_notes && <p className="text-sm text-muted-foreground mt-1">{policy.closure_notes}</p>}
            </div>
            <Button variant="outline" size="sm" onClick={handleUndoClose} disabled={undoingClose} data-testid="undo-close-btn">
              {undoingClose ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Annulla chiusura (errore)
            </Button>
          </div>
        </div>
      )}

      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-border p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-lg bg-primary/15 border-2 border-primary/30 flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">{policyTypeInfo.icon}</span>
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold font-heading">{policy.policy_number}</h1>
                {getStatusBadge(policy)}
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                {policy.company} · {policyTypeInfo.label}
                {policy.license_plate && <span> · <span className="font-mono font-semibold">{policy.license_plate}</span></span>}
              </p>
            </div>
          </div>
        
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setDocumentsOpen(true)} data-testid="documents-btn">
              <FileText className="h-4 w-4 mr-2" />
              Documenti
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/policies?edit=${policy.policy_id}`)} data-testid="edit-btn">
              <Pencil className="h-4 w-4 mr-2" />
              Modifica
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setAdjustmentData(EMPTY_ADJUSTMENT_FORM);
                setAdjustmentOpen(true);
              }}
              className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300"
              data-testid="adjustment-btn"
            >
              <Shield className="h-4 w-4 mr-2" />
              Variazione
            </Button>
            {(policy.premium_adjustments || []).some((v) => (v.type || "") !== "cambio_mezzo") && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleUndoAdjustment}
                disabled={savingAdjustment}
                data-testid="undo-adjustment-btn"
              >
                {savingAdjustment ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Undo2 className="h-4 w-4 mr-2" />
                )}
                Annulla variazione
              </Button>
            )}
            {(policy.not_renewed || policy.status === "not_renewed") && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleUndoNotRenewed}
                disabled={undoingNotRenewed}
                className="border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-800 dark:text-orange-300"
                data-testid="undo-not-renewed-btn"
              >
                {undoingNotRenewed ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                Annulla "non rinnovata"
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Expiry Alert Banner */}
      {(isExpiringSoon || isExpired) && !policy.renewed && !policy.suspended && policy.status !== "cancelled" && (
        <Card className={`border-2 ${isExpired ? 'border-red-500 bg-red-50 dark:bg-red-950' : 'border-orange-500 bg-orange-50 dark:bg-orange-950'}`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className={`h-6 w-6 ${isExpired ? 'text-red-600' : 'text-orange-600'}`} />
                <div>
                  <p className={`font-semibold ${isExpired ? 'text-red-700' : 'text-orange-700'}`}>
                    {isExpired ? '⚠️ Polizza Scaduta!' : `⏰ Scade tra ${daysToExpiry} giorni`}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Scadenza: {formatDate(policy.expiry_date, "-")}
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => {
                  setRenewalData({
                    renewalType: (policy.payment_frequency === "semiannual" && !policy.semiannual_paid) ? "renew_semester" : "same_company",
                    newCompany: "",
                    notRenewedReason: "",
                    newPremium: policy.premium?.toString() || "",
                    newConsultationFee: policy.consultation_fee?.toString() || ""
                  });
                  setRenewalResult(null);
                  setRenewalDialogOpen(true);
                }}
                className="bg-green-600 hover:bg-green-700"
                data-testid="renew-banner-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Gestisci Rinnovo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Policy Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Dettagli Polizza
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Numero Polizza</p>
                  <p className="font-medium font-mono">{policy.policy_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Compagnia</p>
                  <p className="font-medium">{policy.company}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tipo</p>
                  <p className="font-medium flex items-center gap-2">
                    <span>{policyTypeInfo.icon}</span>
                    {policyTypeInfo.label}
                  </p>
                </div>
                {policy.license_plate && (
                  <div>
                    <p className="text-sm text-muted-foreground">Targa</p>
                    <p className="font-medium font-mono">{policy.license_plate}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Frequenza Pagamento</p>
                  <p className="font-medium">
                    {policy.payment_frequency === "semiannual" ? "💰 Semestrale" : "Annuale"}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Decorrenza
                  </p>
                  <p className="font-medium">{formatDate(policy.start_date, "-")}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" /> Scadenza
                  </p>
                  <p className={`font-medium ${isExpiringSoon ? 'text-orange-600' : isExpired ? 'text-red-600' : ''}`}>
                    {formatDate(policy.expiry_date, "-")}
                  </p>
                </div>
                {policy.payment_frequency === "semiannual" && policy.semiannual_expiry && (
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Rata Semestrale
                    </p>
                    <p className={`font-medium ${policy.semiannual_paid ? 'text-green-600' : 'text-orange-600'}`}>
                      {formatDate(policy.semiannual_expiry, "-")}
                      {policy.semiannual_paid ? " (Pagata)" : " (Da pagare)"}
                    </p>
                    {policy.semiannual_paid && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleUndoSemester}
                        disabled={undoingSemester}
                        className="mt-2 h-7 px-2 text-xs"
                        data-testid="undo-semester-inline-btn"
                        title="Annulla il pagamento se hai cliccato per sbaglio"
                      >
                        {undoingSemester ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Undo2 className="h-3 w-3 mr-1" />
                        )}
                        Annulla pagamento
                      </Button>
                    )}
                  </div>
                )}
              </div>

              <Separator />

              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-muted rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Premio</p>
                  <p className="text-xl font-bold text-foreground">{formatCurrency(getPolicyPremiumTotal(policy))}</p>
                  {getServicesTotal(policy) > 0 && (
                    <p className="text-xs text-muted-foreground">incl. servizi</p>
                  )}
                </div>
                <div className="p-3 bg-muted rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Consulenza</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(policy.consultation_fee)}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Totale</p>
                  <p className="text-xl font-bold text-foreground">
                    {formatCurrency(getPolicyGrandTotal(policy))}
                  </p>
                </div>
              </div>

              {/* Servizi Aggiuntivi */}
              <Separator />
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Servizi Aggiuntivi
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowAddService(!showAddService)}
                    data-testid="add-service-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Aggiungi
                  </Button>
                </div>

                {showAddService && (
                  <div className="p-3 bg-muted rounded-lg mb-3 space-y-3" data-testid="add-service-form">
                    {/* Selezione dal catalogo */}
                    {servicesCatalog.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Seleziona dal catalogo</p>
                        <div className="grid grid-cols-1 gap-1.5 max-h-40 overflow-y-auto">
                          {servicesCatalog.map((svc) => (
                            <button
                              key={svc.service_id}
                              className="flex items-center justify-between p-2 rounded-md border border-border hover:bg-background text-left text-sm transition-colors"
                              data-testid={`select-catalog-service-${svc.service_id}`}
                              onClick={() => {
                                setNewService({
                                  provider: svc.provider,
                                  description: svc.description,
                                  cost: String(svc.default_cost || 0)
                                });
                              }}
                            >
                              <span><span className="font-medium">{svc.provider}</span> — {svc.description}</span>
                              <span className="text-muted-foreground ml-2">{formatCurrency(svc.default_cost)}</span>
                            </button>
                          ))}
                        </div>
                        <Separator className="my-2" />
                        <p className="text-xs text-muted-foreground">oppure inserisci manualmente:</p>
                      </div>
                    )}
                    {/* Input manuale / pre-compilato dal catalogo */}
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        className="px-3 py-2 rounded-md border border-border bg-background text-sm"
                        placeholder="Fornitore"
                        value={newService.provider}
                        onChange={(e) => setNewService({...newService, provider: e.target.value})}
                        data-testid="service-provider-input"
                      />
                      <input
                        className="px-3 py-2 rounded-md border border-border bg-background text-sm"
                        placeholder="Descrizione servizio"
                        value={newService.description}
                        onChange={(e) => setNewService({...newService, description: e.target.value})}
                        data-testid="service-description-input"
                      />
                      <input
                        className="px-3 py-2 rounded-md border border-border bg-background text-sm"
                        placeholder="Costo (€)"
                        type="number"
                        step="0.01"
                        value={newService.cost}
                        onChange={(e) => setNewService({...newService, cost: e.target.value})}
                        data-testid="service-cost-input"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => { setShowAddService(false); setNewService({ provider: "", description: "", cost: "" }); }}>Annulla</Button>
                      <Button size="sm" onClick={handleAddService} disabled={savingService} data-testid="save-service-btn">
                        {savingService ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                        Salva
                      </Button>
                    </div>
                  </div>
                )}

                {(policy.additional_services || []).length > 0 ? (
                  <div className="space-y-2">
                    {policy.additional_services.map((service, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded-lg text-sm" data-testid={`service-item-${idx}`}>
                        <div className="flex-1">
                          <span className="font-medium">{service.provider}</span>
                          <span className="text-muted-foreground mx-2">—</span>
                          <span>{service.description}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{formatCurrency(service.cost)}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveService(idx)}
                            data-testid={`remove-service-${idx}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="text-right text-sm text-muted-foreground pt-1">
                      Totale servizi: <span className="font-semibold text-foreground">
                        {formatCurrency(policy.additional_services.reduce((sum, s) => sum + (s.cost || 0), 0))}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground/60 text-center py-2">Nessun servizio aggiuntivo</p>
                )}
              </div>

              {policy.notes && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Note</p>
                    <p className="text-sm bg-muted p-3 rounded-lg">{policy.notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons Card */}
          <Card>
            <CardHeader>
              <CardTitle>Azioni Polizza</CardTitle>
              <CardDescription>Gestisci rinnovi, sospensioni e altre operazioni</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {/* Renewal Button - Always visible and prominent */}
                <Button 
                  onClick={() => {
                    setRenewalData({
                      renewalType: (policy.payment_frequency === "semiannual" && !policy.semiannual_paid) ? "renew_semester" : "same_company",
                      newCompany: "",
                      notRenewedReason: "",
                      newPremium: policy.premium?.toString() || "",
                      newConsultationFee: policy.consultation_fee?.toString() || ""
                    });
                    setRenewalResult(null);
                  setRenewalDialogOpen(true);
                  }}
                  className="bg-green-600 hover:bg-green-700 h-auto py-3"
                  disabled={policy.not_renewed || isClosed}
                  data-testid="renew-btn"
                >
                  <div className="flex flex-col items-center gap-1">
                    <RefreshCw className="h-5 w-5" />
                    <span className="text-xs">Rinnovo</span>
                  </div>
                </Button>

                {/* Replace Vehicle Button - only for vehicle policies */}
                {isVehiclePolicy && !policy.suspended && !isClosed && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setReplaceVehicleData(EMPTY_VEHICLE_FORM);
                      setReplaceVehicleOpen(true);
                    }}
                    className="h-auto py-3 border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="replace-vehicle-btn"
                  >
                    <div className="flex flex-col items-center gap-1">
                      <Car className="h-5 w-5" />
                      <span className="text-xs">Sostituisci Mezzo</span>
                    </div>
                  </Button>
                )}

                {/* Suspend/Reactivate Button */}
                {isSuspendable && (
                  policy.suspended ? (
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setReactivationData({ new_start_date: "", new_expiry_date: "" });
                        setReactivateDialogOpen(true);
                      }}
                      className="h-auto py-3 border-green-300 text-green-700 hover:bg-green-50"
                      data-testid="reactivate-btn"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <Play className="h-5 w-5" />
                        <span className="text-xs">Riattiva</span>
                      </div>
                    </Button>
                  ) : (
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setSuspensionData({ suspension_reminder: "", suspension_reason: "" });
                        setSuspendDialogOpen(true);
                      }}
                      className="h-auto py-3 border-orange-300 text-orange-700 hover:bg-orange-50"
                      data-testid="suspend-btn"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <Pause className="h-5 w-5" />
                        <span className="text-xs">Sospendi</span>
                      </div>
                    </Button>
                  )
                )}

                {/* Chiusura anticipata (vendita veicolo, demolizione, recesso...) */}
                {!isClosed && !policy.renewed && (
                  <Button
                    variant="outline"
                    onClick={() => setCloseDialogOpen(true)}
                    className="h-auto py-3 border-gray-400 text-gray-700 hover:bg-gray-100 dark:text-gray-300"
                    data-testid="close-policy-btn"
                  >
                    <div className="flex flex-col items-center gap-1">
                      <XCircle className="h-5 w-5" />
                      <span className="text-xs">Chiudi polizza</span>
                    </div>
                  </Button>
                )}

                {/* Documents Button */}
                <Button
                  variant="outline"
                  onClick={() => setDocumentsOpen(true)}
                  className="h-auto py-3"
                  data-testid="docs-action-btn"
                >
                  <div className="flex flex-col items-center gap-1">
                    <FileText className="h-5 w-5" />
                    <span className="text-xs">Documenti</span>
                  </div>
                </Button>

                {/* Export Button */}
                <Button 
                  variant="outline"
                  onClick={() => toast.info("Export PDF in arrivo...")}
                  className="h-auto py-3"
                  data-testid="export-btn"
                >
                  <div className="flex flex-col items-center gap-1">
                    <Download className="h-5 w-5" />
                    <span className="text-xs">Esporta</span>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Client Info */}
        <div className="space-y-6">
          {/* Client Card */}
          {client && (
            <Card 
              className="cursor-pointer hover:shadow-md hover:border-primary/30 transition-all group"
              onClick={() => navigate(`/clients/${client.client_id}`)}
              data-testid="client-card"
            >
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wide">
                  {client.client_type === "business" ? (
                    <Building2 className="h-4 w-4" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                  Cliente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="font-semibold text-lg group-hover:text-primary transition-colors">{client.name}</p>
                  <p className="text-sm text-muted-foreground font-mono">{client.fiscal_code}</p>
                </div>

                {client.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{client.phone}</span>
                  </div>
                )}

                {client.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">{client.email}</span>
                  </div>
                )}

                <Separator />

                {/* Contact Buttons */}
                <div className="grid grid-cols-3 gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline" size="sm" onClick={handleCall} data-testid="call-btn">
                    <Phone className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleWhatsApp} className="text-green-600" data-testid="whatsapp-btn">
                    <MessageCircle className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleEmail} data-testid="email-btn">
                    <Mail className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Document Preview - Policy docs */}
          <DocumentPreviewCard
            entityType="policy"
            entityId={policy.policy_id}
            entityName={`Polizza ${policy.policy_number}`}
            onOpenAll={() => setDocumentsOpen(true)}
            maxItems={4}
          />

          {/* Suspension Info */}
          {policy.suspended && (
            <Card className="border-orange-300 bg-orange-50 dark:bg-orange-950">
              <CardHeader>
                <CardTitle className="text-orange-700 flex items-center gap-2">
                  <Pause className="h-5 w-5" />
                  Polizza Sospesa
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {policy.suspension_reason && (
                  <div>
                    <p className="text-muted-foreground">Motivo:</p>
                    <p className="font-medium">{policy.suspension_reason}</p>
                  </div>
                )}
                {policy.suspension_reminder && (
                  <div>
                    <p className="text-muted-foreground">Promemoria:</p>
                    <p className="font-medium">{formatDate(policy.suspension_reminder, "-")}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Renewal History */}
          {policy.renewal_history && policy.renewal_history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Storico Rinnovi
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {policy.renewal_history.slice(-3).map((r, idx) => (
                    <div key={idx} className="flex justify-between text-muted-foreground">
                      <span>{formatDate(r.date, "-")}</span>
                      <span>{r.type}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Vehicle History */}
          {(policy.vehicle_history || []).length > 0 && (
            <Card data-testid="vehicle-history-card">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Car className="h-4 w-4" />
                  Storico Veicoli
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  {/* Current plate */}
                  <div className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                    <span className="font-mono font-semibold text-green-700 dark:text-green-300">{policy.license_plate}</span>
                    <Badge variant="outline" className="text-green-600 border-green-300 text-xs">Attuale</Badge>
                  </div>
                  {/* Old plates */}
                  {[...policy.vehicle_history].reverse().map((vh, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded-lg" data-testid={`vehicle-history-${idx}`}>
                      <div>
                        <span className="font-mono font-medium text-muted-foreground">{vh.plate}</span>
                        {vh.notes && <p className="text-xs text-muted-foreground mt-0.5">{vh.notes}</p>}
                      </div>
                      <span className="text-xs text-muted-foreground">{formatDate(vh.replaced_date, "-")}</span>
                    </div>
                  ))}
                  {policy.vehicle_history.length > 0 && (
                    <div className="pt-2 border-t space-y-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleUndoReplaceVehicle}
                        disabled={undoingVehicle}
                        data-testid="undo-replace-vehicle-btn"
                      >
                        {undoingVehicle ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Undo2 className="h-4 w-4 mr-2" />
                        )}
                        Annulla ultima sostituzione
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        Ripristina targa, premio e consulenza precedenti e toglie il conguaglio dai report.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* v3.9.12 — storico modifiche della polizza */}
          <AuditHistory entityType="policy" entityId={policy.policy_id} />
        </div>
      </div>

      {/* Dialogs */}
      <RenewalPolicyDialog
        open={renewalDialogOpen}
        onOpenChange={setRenewalDialogOpen}
        policy={policy}
        getClientName={getClientName}
        renewalData={renewalData}
        setRenewalData={setRenewalData}
        onRenew={handleRenewal}
        onUndo={handleUndoSemester}
        undoing={undoingSemester}
        saving={saving}
        renewalResult={renewalResult}
        uploading={uploadingAttach}
        onAttachDocument={allegaDocumentoRinnovo}
        onSkipAttach={saltaAllegato}
      />

      <SuspendPolicyDialog
        open={suspendDialogOpen}
        onOpenChange={setSuspendDialogOpen}
        policy={policy}
        getClientName={getClientName}
        suspensionData={suspensionData}
        setSuspensionData={setSuspensionData}
        onSuspend={handleSuspend}
      />

      <ClosePolicyDialog
        open={closeDialogOpen}
        onOpenChange={setCloseDialogOpen}
        policy={policy}
        clientName={getClientName(policy.client_id)}
        onClosed={fetchPolicy}
      />

      <ReactivatePolicyDialog
        open={reactivateDialogOpen}
        onOpenChange={setReactivateDialogOpen}
        policy={policy}
        getClientName={getClientName}
        reactivationData={reactivationData}
        setReactivationData={setReactivationData}
        onReactivate={handleReactivate}
      />

      {/* Documents Dialog */}
      <DocumentsDialog
        open={documentsOpen}
        onOpenChange={setDocumentsOpen}
        entityType="policy"
        entityId={policy.policy_id}
        entityName={`Polizza ${policy.policy_number}`}
      />

      {/* Variazione in corso d'anno (appendice di variazione) */}
      <Dialog open={adjustmentOpen} onOpenChange={setAdjustmentOpen}>
        <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-violet-500" />
              Variazione in corso d'anno
            </DialogTitle>
            <DialogDescription>
              Aggiunta o riduzione di garanzie durante l'anno. Il premio della polizza si aggiorna
              subito, e la differenza di prezzo viene registrata nel mese della decorrenza.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Premio attuale</span>
                <span className="font-mono font-semibold">{formatCurrency(policy.premium || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Consulenza attuale</span>
                <span className="font-mono">{formatCurrency(policy.consultation_fee || 0)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adj_description">Cosa cambia *</Label>
              <Input
                id="adj_description"
                value={adjustmentData.description}
                onChange={(e) => setAdjustmentData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="es. Aggiunta garanzia furto e incendio"
                data-testid="adjustment-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="adj_premium">Differenza premio</Label>
                <Input
                  id="adj_premium"
                  type="number"
                  step="0.01"
                  value={adjustmentData.premium_adjustment}
                  onChange={(e) => setAdjustmentData((prev) => ({ ...prev, premium_adjustment: e.target.value }))}
                  placeholder="es. 120 oppure -80"
                  className="font-mono"
                  data-testid="adjustment-premium"
                />
                <p className="text-xs text-muted-foreground">
                  Positiva = supplemento, negativa = rimborso
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="adj_cons">Differenza consulenza</Label>
                <Input
                  id="adj_cons"
                  type="number"
                  step="0.01"
                  value={adjustmentData.consultation_adjustment}
                  onChange={(e) => setAdjustmentData((prev) => ({ ...prev, consultation_adjustment: e.target.value }))}
                  placeholder="facoltativa"
                  className="font-mono"
                  data-testid="adjustment-consultation"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="adj_date">Decorrenza</Label>
                <Input
                  id="adj_date"
                  type="date"
                  value={adjustmentData.effective_date}
                  onChange={(e) => setAdjustmentData((prev) => ({ ...prev, effective_date: e.target.value }))}
                  data-testid="adjustment-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="adj_appendix">N. appendice</Label>
                <Input
                  id="adj_appendix"
                  value={adjustmentData.appendix_number}
                  onChange={(e) => setAdjustmentData((prev) => ({ ...prev, appendix_number: e.target.value }))}
                  placeholder="es. A/1"
                  data-testid="adjustment-appendix"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adj_notes">Note (facoltative)</Label>
              <Textarea
                id="adj_notes"
                value={adjustmentData.notes}
                onChange={(e) => setAdjustmentData((prev) => ({ ...prev, notes: e.target.value }))}
                rows={2}
                data-testid="adjustment-notes"
              />
            </div>

            <div className="p-3 border border-violet-300/60 bg-violet-50/50 dark:bg-violet-950/20 rounded-lg text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Premio dopo la variazione</span>
                <span className="font-mono font-semibold" data-testid="adjustment-preview-premium">
                  {formatCurrency(
                    (Number(policy.premium) || 0) + (parseFloat(adjustmentData.premium_adjustment) || 0)
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Consulenza dopo la variazione</span>
                <span className="font-mono font-semibold">
                  {formatCurrency(
                    (Number(policy.consultation_fee) || 0) +
                      (parseFloat(adjustmentData.consultation_adjustment) || 0)
                  )}
                </span>
              </div>
              <p className="text-xs text-muted-foreground pt-1">
                Nel Portafoglio la differenza comparirà come riga "VAR" nel mese della decorrenza.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustmentOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleAddAdjustment}
              disabled={savingAdjustment}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="adjustment-save"
            >
              {savingAdjustment ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Registra variazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace Vehicle Dialog */}
      <Dialog open={replaceVehicleOpen} onOpenChange={setReplaceVehicleOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="h-5 w-5 text-blue-500" />
              Sostituzione Mezzo
            </DialogTitle>
            <DialogDescription>
              Il vecchio mezzo verrà archiviato nello storico
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {policy.license_plate && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Targa attuale</p>
                <p className="font-mono font-semibold text-lg">{policy.license_plate}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="new_plate">Nuova Targa *</Label>
              <Input
                id="new_plate"
                value={replaceVehicleData.new_plate}
                onChange={(e) => setReplaceVehicleData(prev => ({ ...prev, new_plate: e.target.value.toUpperCase() }))}
                placeholder="es. AB123CD"
                className="uppercase font-mono"
                data-testid="new-plate-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_vehicle_desc">Nuovo mezzo (opzionale)</Label>
              <Input
                id="new_vehicle_desc"
                value={replaceVehicleData.new_vehicle_desc}
                onChange={(e) => setReplaceVehicleData(prev => ({ ...prev, new_vehicle_desc: e.target.value }))}
                placeholder="es. Fiat Ducato 2.3 130cv"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="effective_date">Decorrenza</Label>
                <Input
                  id="effective_date"
                  type="date"
                  value={replaceVehicleData.effective_date}
                  onChange={(e) => setReplaceVehicleData(prev => ({ ...prev, effective_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="appendix_number">N. appendice</Label>
                <Input
                  id="appendix_number"
                  value={replaceVehicleData.appendix_number}
                  onChange={(e) => setReplaceVehicleData(prev => ({ ...prev, appendix_number: e.target.value }))}
                  placeholder="es. 12345"
                />
              </div>
            </div>

            {/* CONGUAGLIO PREMIO — mezzo piu grande = supplemento */}
            <div className="p-3 border border-amber-300/50 bg-amber-50/50 rounded-lg space-y-3">
              <p className="text-sm font-medium text-amber-800">
                Conguaglio premio
                {policy.premium ? (
                  <span className="font-normal text-muted-foreground"> — attuale: {policy.premium} EUR</span>
                ) : null}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Modalita</Label>
                  <select
                    className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                    value={replaceVehicleData.adj_mode}
                    onChange={(e) => setReplaceVehicleData(prev => ({ ...prev, adj_mode: e.target.value }))}
                  >
                    <option value="none">Nessun conguaglio</option>
                    <option value="percent">Percentuale (%)</option>
                    <option value="amount">Importo (+/-)</option>
                    <option value="full">Nuovo premio pieno</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    {replaceVehicleData.adj_mode === "percent" ? "Percentuale" :
                     replaceVehicleData.adj_mode === "full" ? "Nuovo premio" : "Importo"}
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    disabled={replaceVehicleData.adj_mode === "none"}
                    value={replaceVehicleData.adj_value}
                    onChange={(e) => setReplaceVehicleData(prev => ({ ...prev, adj_value: e.target.value }))}
                    placeholder={replaceVehicleData.adj_mode === "percent" ? "es. 15" : "es. 180"}
                  />
                </div>
              </div>
              {replaceVehicleData.adj_mode !== "none" && !isNaN(parseFloat(replaceVehicleData.adj_value)) && policy.premium ? (
                <p className="text-xs text-amber-900">
                  Nuovo premio: {formatCurrency((
                    replaceVehicleData.adj_mode === "percent"
                      ? policy.premium + (policy.premium * parseFloat(replaceVehicleData.adj_value) / 100)
                      : replaceVehicleData.adj_mode === "amount"
                        ? policy.premium + parseFloat(replaceVehicleData.adj_value)
                        : parseFloat(replaceVehicleData.adj_value)
                  ))}
                </p>
              ) : null}
              <div className="space-y-1">
                <Label className="text-xs">Conguaglio consulenza (opzionale)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={replaceVehicleData.consultation_adjustment}
                  onChange={(e) => setReplaceVehicleData(prev => ({ ...prev, consultation_adjustment: e.target.value }))}
                  placeholder="es. 20"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="replace_notes">Note (opzionale)</Label>
              <Textarea
                id="replace_notes"
                value={replaceVehicleData.notes}
                onChange={(e) => setReplaceVehicleData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="es. Venduto vecchio veicolo, acquistato nuovo..."
                rows={2}
                data-testid="replace-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceVehicleOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleReplaceVehicle}
              disabled={savingVehicle || !replaceVehicleData.new_plate.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-replace-vehicle-btn"
            >
              {savingVehicle ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Car className="h-4 w-4 mr-2" />}
              Sostituisci
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
